import React, { FC, useState, useEffect } from 'react';

import {
  makeStyles,
  Grid,
  Typography,
  Button,
  Slider
} from '@material-ui/core';

import clsx from 'clsx';
import { teal } from '@material-ui/core/colors';
import CurrencyFormat from '../../../Core/CurrencyFormat/CurrencyFormat';
import KeyboardField from '../../../Core/KeyboardField/KeyboardField';
import { useRef } from 'react';

const useStyles = makeStyles(theme => ({
  tipGuide: {
    width: '25%'
  },
  tipTitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
  },
  container: {
    margin: 'auto',
    maxWidth: 420
  },
  active: {
    backgroundColor: teal[100],
    '&:hover': {
      backgroundColor: teal[200]
    }
  },
  note: {
    marginTop: theme.spacing(4)
  },
  slideTip: {
    marginTop: theme.spacing(4)
  }
}));

const defaultNoteForOnlineOrdering =
  'Thank you for supporting your local businesses. You are making a huge difference in helping this community.';

interface TipGuidesProps {
  value?: number;
  onChange?: (value: number) => void;
  onChangeDefaultTipForDriver?: (value: number) => void;
  total?: number;
  tipGuides?: number[];
  isVirtualKeyboard?: boolean;
  defaultTipGuide?: number;
  note?: string;
  splitTip?: boolean;
  defaultTipForDriver?: number;
  noteForOnlineOrdering?: string;
}

const TipGuides: FC<TipGuidesProps> = ({
  value,
  onChange,
  total,
  tipGuides,
  defaultTipGuide,
  note,
  noteForOnlineOrdering,
  defaultTipForDriver,
  onChangeDefaultTipForDriver,
  splitTip,
  isVirtualKeyboard
}) => {
  const classes = useStyles();
  const amount = total ?? 0;
  const guides = tipGuides ?? [];
  const defaultValue =
    defaultTipGuide === 0
      ? guides[0] * amount
      : guides[defaultTipGuide ?? 0] * amount;
  const [state, setState] = useState({
    value: value ?? defaultValue,
    selectedTipGuide: defaultTipGuide ?? -1,
    defaultTipForDriver: defaultTipForDriver
  });

  const setTipAmount = (tipAmount: number, selectedTipGuide: number = -1) => {
    setState({ ...state, value: tipAmount, selectedTipGuide });
    if (onChange) {
      onChange(tipAmount);
    }
  };

  useEffect(() => {
    if (onChange) {
      const current = value || defaultValue;
      setTipAmount(
        current,
        guides.findIndex(e => (e * amount).toFixed(3) === current.toFixed(3))
      );
    }
    // eslint-disable-next-line
  }, [onChange]);

  const currencyFieldRef = useRef<any>();

  return (
    <Grid container direction="column" className={classes.container}>
      <KeyboardField
        ref={currencyFieldRef}
        margin="dense"
        label="Tip amount"
        value={state.value}
        handleValueChange={(v: number) => setTipAmount(v)}
        placeholder="0.00"
        minimumValue="0"
        keyboard={{
          enable: isVirtualKeyboard ?? false,
          layout: {
            default: ['1 2 3 4 5 6 7 8 9 0 {bksp}']
          }
        }}
      />
      <Typography
        variant="h6"
        color="textSecondary"
        className={classes.tipTitle}
      >
        Popular choices
      </Typography>
      <Grid item container direction="row" justify="space-between">
        {guides.map((tip, index) => [
          <Button
            key={tip}
            onClick={() => {
              setTipAmount(amount * tip, index);
              currencyFieldRef.current.clearInput();
            }}
            size="large"
            color="primary"
            variant="outlined"
            className={clsx(classes.tipGuide, {
              [classes.active]: index === state.selectedTipGuide
            })}
          >
            {`${(tip * 100).toFixed(2)}%`}
          </Button>
        ])}
      </Grid>
      {!(splitTip && typeof state.defaultTipForDriver === 'number') || (
        <Grid className={classes.slideTip} container>
          <Grid item xs={12} className={classes.slideTip}>
            <Slider
              defaultValue={defaultTipForDriver}
              valueLabelDisplay="on"
              step={10}
              marks
              min={0}
              max={100}
              onChange={(_, val) => {
                onChangeDefaultTipForDriver?.(val as number);
                onChange?.(state.value);
                setState({ ...state, defaultTipForDriver: val as number });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>
              Tip for Delivery Driver ({state.defaultTipForDriver}%):
              <CurrencyFormat
                prefix=" "
                amount={state.value * (state.defaultTipForDriver / 100)}
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>
              Tip for Staff ({100 - state.defaultTipForDriver}%):
              <CurrencyFormat
                prefix=" "
                amount={state.value * ((100 - state.defaultTipForDriver) / 100)}
              />
            </Typography>
          </Grid>
        </Grid>
      )}

      {note && (
        <Typography color="textSecondary" className={classes.note}>
          {note}
        </Typography>
      )}

      <Typography color="textSecondary" className={classes.note}>
        {noteForOnlineOrdering || defaultNoteForOnlineOrdering}
      </Typography>
    </Grid>
  );
};

export default TipGuides;
